import {
  AcceptedMessageDataToSubmitMessage,
  AcceptedMessageTypeToSubmitMessage,
  ChatMessageFileData,
  ChatMessageImageOrVideoData,
} from '../contexts/chatSocket/hooks/useChatWebsocket/useChatWebsocket.types';

export enum MessageType {
  FILE = 'FILE',
  MESSAGE = 'MESSAGE',
  VIDEO_CALL = 'VIDEO_CALL',
  IMAGE_OR_VIDEO = 'IMAGE_OR_VIDEO',
}

export interface VideoCall {
  id: string;
  meetingId: string;
  startDate: Date;
  endDate: Date;
  roomUrl: string;
  hostRoomUrl?: string;
  viewerRoomUrl?: string;
  active: boolean;
  chat: { id: string };
}

export interface SubmitMessage {
  type: AcceptedMessageTypeToSubmitMessage;
  body: string;
  chatId: string;
  tempId: string;
  data?: AcceptedMessageDataToSubmitMessage;
}

interface ChatMessageCommonParams {
  chat: Chat;
  body: string;
  createdAt: Date;
  updatedAt: Date;
  commercialName: string;
  user: ChatUser;
  id?: string;
}

export interface ImageOrVideoChatMessage extends ChatMessageCommonParams {
  type: MessageType.IMAGE_OR_VIDEO;
  data: ChatMessageImageOrVideoData;
}

export interface FileChatMessage extends ChatMessageCommonParams {
  type: MessageType.FILE;
  data: ChatMessageFileData;
}

export interface VideoCallChatMessage extends ChatMessageCommonParams {
  type: MessageType.VIDEO_CALL;
  data: VideoCall;
}

export interface MessageChatMessage extends ChatMessageCommonParams {
  type: MessageType.MESSAGE;
  data: undefined;
}

export type ChatMessage =
  | FileChatMessage
  | MessageChatMessage
  | VideoCallChatMessage
  | ImageOrVideoChatMessage;

export type CreatedMessageResponse =
  | {
      ok: true;
      message: ChatMessage;
    }
  | {
      ok: false;
      message: SubmitMessage;
    };

export type ChatBrandStore = {
  id: string;
  commercialName: string;
  logoUrl?: string;
};

interface ChatLastMessage {
  body: string;
  updatedAt: Date;
  type: MessageType;
  authorEmail: string;
  authorName?: string;
}

export enum ChatType {
  GROUP = 'GROUP',
  TOPIC = 'TOPIC',
  PRIVATE = 'PRIVATE',
}

export interface Chat {
  id: string;
  title: string;
  type: ChatType;
  createdAt: Date;
  updatedAt: Date;
  brand: ChatBrandStore;
  stores: ChatBrandStore[];
  caseId?: string;
  inProgressVideoCall?: VideoCall;
  lastMessage?: ChatLastMessage;
}

export interface ChatParticipant {
  id: string;
  commercialName: string;
  teamType: 'brand' | 'store';
  logoUrl?: string;
  companyName?: string;
  privateChatId?: string;
}

export interface ChatUser {
  id: string;
  email: string;
  name?: string;
  surname?: string;
  profileUrl?: string;
}

export type ChatRoomFormValues = {
  title?: string;
  participants: ChatParticipant[];
};

export interface CreateChatParticipant {
  id: string;
}

export type CreateChatParticipants = {
  brand?: CreateChatParticipant;
  stores?: CreateChatParticipant[];
};

export type CreateChatDto = {
  type: ChatType;
  title?: string;
  caseId?: string;
  participants?: CreateChatParticipants;
};

export type UpdateChatData = {
  title?: string;
  participants?: CreateChatParticipants;
};

export type GetChatToUpdate = Chat & {
  participants: {
    brand: ParticipantInitialValue;
    stores: ParticipantInitialValue[];
  };
};

type ParticipantInitialValue = {
  id: string;
  commercialName?: string;
};

export type ChatRoomInitialParticipants = {
  brand: ParticipantInitialValue;
  stores: ParticipantInitialValue[];
};

export type ChatRoomInitialValues = {
  title?: string;
  participants: ChatRoomInitialParticipants;
};
